import React from "react";
import { Box, Container, Grid, Card, CardContent, Typography } from "@mui/material";

/**
 * ✅ Local images
 */
import cs1 from "../assets/vision.png";
import cs2 from "../assets/mission.png";

/**
 * ✅ Background image
 */
import bgImg from "../assets/msback.png";

const services = [
  {
    title: "Our Vision",
    description:
      "To become a globally trusted MedTech innovator by creating clinically aligned technologies that enhance patient safety, improve clinical outcomes, and support faster, smarter decision-making. We aim to deliver solutions that are reliable, usable, and scalable across hospitals and future care settings.",
    image: cs1,
  },
  {
    title: "Our Mission",
    description:
      "To engineer medical technologies with strong clinical grounding by combining robust hardware, intelligent software, and rigorous validation. Our mission is to support healthcare teams by improving workflow efficiency, reducing risk, and enabling consistent, high-quality patient care through evidence-driven development.",
    image: cs2,
  },
];

function MissionVisionSection() {
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        position: "relative",
        backgroundImage: `url(${bgImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
      marginTop="-3px"
    >
      {/* ✅ Overlay for readability */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(2px)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Heading */}
        <Typography variant="h4" align="center" fontWeight={800} gutterBottom>
          Our Mission & Vision
        </Typography>

        {/* Intro */}
        <Typography
          variant="body1"
          align="center"
          color="text.secondary"
          sx={{ maxWidth: 820, mx: "auto", mb: 6, lineHeight: 1.8 }}
        >
          We exist to solve meaningful clinical problems with technology that is safe,
          validated, and practical for real healthcare workflows. Our vision defines
          where we are going, and our mission defines how we build—step by step—with
          clarity, evidence, and speed.
        </Typography>

        {/* Cards */}
        <Grid container spacing={4} justifyContent="center">
          {services.map((service, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={5}
              key={index}
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Card
                sx={{
                  maxWidth: 430,
                  width: "100%",
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "0.3s ease",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: 6,
                  },
                }}
              >
                {/* Image */}
                <Box
                  component="img"
                  src={service.image}
                  alt={service.title}
                  sx={{
                    width: "100%",
                    height: 220,
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                {/* Content */}
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="h6" fontWeight={800} gutterBottom>
                    {service.title}
                  </Typography>

                  {/* ✅ Goal only for second card (Our Mission) */}
                  {index === 1 && (
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}
                    >
                      Goal: Transform clinical needs into safe, validated, and deployable medical technology.
                    </Typography>
                  )}

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.8 }}
                  >
                    {service.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default MissionVisionSection;
